import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarDays, Calendar as CalendarIcon, Check, Copy, Loader2, Mail, Send, Sparkles, Link as LinkIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Logo, LoadingMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { AssetCard } from "@/components/studio/AssetCard";
import { PerformanceTab } from "@/components/studio/PerformanceTab";
import { HistoryTab } from "@/components/studio/HistoryTab";
import { ScheduleTab } from "@/components/studio/ScheduleTab";
import { ShareImagePanel } from "@/components/studio/ShareImagePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const CHANNELS = [
  { id: "linkedin", label: "LinkedIn" },
  { id: "x", label: "X" },
  { id: "producthunt", label: "Product Hunt" },
  { id: "email", label: "Email" },
  { id: "ads", label: "Ads" },
  { id: "landing", label: "Landing" },
  { id: "seo", label: "SEO brief" },
  { id: "outreach", label: "Outreach" },
];

const VOICES = [
  "Precision editorial",
  "Founder-led and direct",
  "Bold conversion",
  "Technical diligence",
];

const STATUS_OPTIONS = ["draft", "scheduled", "published", "archived"];

const initialForm = {
  name: "Doclyx V4 launch",
  goal: "Convert M&A and legal professionals into Professional trials",
  audience: "M&A analysts, legal operations leaders, deal teams, and founders managing high-volume document work",
  offer: "Professional $50/month with a 14-day trial, unlimited conversions, batch processing, redaction, OCR, and PDF-to-DOCX",
  voice: VOICES[0],
  proof: "Use only real product facts: 16 engines, paid workspace, permanent redaction, visual page ordering, Word export, AES-256 positioning, and no fake customers or metrics.",
  notes: "",
  channels: ["linkedin", "x", "producthunt", "email", "landing"],
  launch_date: new Date().toISOString().slice(0, 10),
};

const fieldClass = "w-full rounded-md bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:border-white/30 transition-colors duration-300";
const labelClass = "font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400 block mb-2";

const plain = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
};

export default function AdminWingman() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [campaign, setCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignStats, setCampaignStats] = useState(null);
  const [activeTab, setActiveTab] = useState("plan");

  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [previewStatus, setPreviewStatus] = useState(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [refining, setRefining] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [sendState, setSendState] = useState(null);

  const token = useCallback(() => localStorage.getItem("doclyx_token"), []);
  const headers = useCallback(() => ({ Authorization: `Bearer ${token()}` }), [token]);

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && user.role !== "admin") navigate("/account");
  }, [user, navigate]);

  const loadCampaigns = useCallback(async () => {
    const res = await axios.get(`${API}/wingman/campaigns`, { headers: headers(), withCredentials: true });
    setCampaigns(res.data.campaigns || []);
  }, [headers]);

  useEffect(() => {
    if (user?.role === "admin") loadCampaigns().catch(() => {});
  }, [user, loadCampaigns]);

  useEffect(() => {
    if (!campaign?.slug || user?.role !== "admin") return;
    axios.get(`${API}/admin/stats`, { headers: headers(), withCredentials: true })
      .then((res) => setCampaignStats(res.data.funnel?.campaigns?.[campaign.slug] || null))
      .catch(() => setCampaignStats(null));
  }, [campaign?.slug, user, headers]);

  const tabs = useMemo(() => {
    if (!campaign) return [];
    const requestChannels = campaign.request?.channels || Object.keys(campaign.campaign?.assets || {});
    return ["plan", "schedule", ...requestChannels, "personas", "utm", "performance", "history"];
  }, [campaign]);

  const openCampaign = useCallback(async (id, { keepTab = false } = {}) => {
    const res = await axios.get(`${API}/wingman/campaigns/${id}`, { headers: headers(), withCredentials: true });
    setCampaign(res.data);
    if (!keepTab) setActiveTab("plan");
    return res.data;
  }, [headers]);

  if (!user || user.role !== "admin") {
    return (
      <main className="v4-theme bg-page min-h-screen flex items-center justify-center">
        <LoadingMark label="Loading marketing studio" />
      </main>
    );
  }

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggleChannel = (id) => setForm((f) => ({
    ...f,
    channels: f.channels.includes(id) ? f.channels.filter((c) => c !== id) : [...f.channels, id],
  }));

  const stepValid = () => {
    if (step === 0) return form.name.trim().length >= 3 && form.goal.trim().length >= 10 && form.audience.trim().length >= 5;
    if (step === 1) return form.offer.trim().length >= 5 && form.voice.trim().length >= 3;
    return form.channels.length > 0;
  };

  const selectedDate = form.launch_date ? new Date(`${form.launch_date}T00:00:00`) : undefined;

  const generate = async () => {
    if (generating || !stepValid()) return;
    setGenerating(true);
    setError(null);
    setPreviewStatus(null);
    try {
      const res = await axios.post(`${API}/wingman/campaign`, form, { headers: headers(), withCredentials: true, timeout: 120000 });
      setCampaign(res.data);
      setActiveTab("plan");
      setStep(0);
      await loadCampaigns();
    } catch (e) {
      setError(e.response?.data?.detail || "Campaign generation failed. Try a tighter brief.");
    }
    setGenerating(false);
  };

  const openSaved = async (id) => {
    setError(null);
    setSendState(null);
    try {
      await openCampaign(id);
    } catch {
      setError("Could not load that campaign.");
    }
  };

  const saveStatus = async (status) => {
    if (!campaign || statusSaving) return;
    setStatusSaving(true);
    try {
      await axios.patch(`${API}/wingman/campaigns/${campaign.campaign_id}/status`, { status }, { headers: headers(), withCredentials: true });
      setCampaign((c) => ({ ...c, status }));
      setCampaigns((items) => items.map((c) => c.campaign_id === campaign.campaign_id ? { ...c, status } : c));
    } catch {
      setError("Could not update campaign status.");
    }
    setStatusSaving(false);
  };

  const toggleChecklist = async (index, done) => {
    if (!campaign) return;
    const nextChecklist = campaign.checklist.map((item, i) => i === index ? { ...item, done } : item);
    setCampaign((c) => ({ ...c, checklist: nextChecklist }));
    try {
      await axios.patch(`${API}/wingman/campaigns/${campaign.campaign_id}/checklist/${index}`, { done }, { headers: headers(), withCredentials: true });
    } catch {
      setError("Could not save checklist state.");
    }
  };

  const copy = async (key, value) => {
    const text = plain(value);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const area = document.createElement("textarea");
      area.value = text;
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      let ok = false;
      try {
        ok = document.execCommand("copy");
      } catch {
        ok = false;
      }
      area.remove();
      if (!ok) {
        setError("Copy is unavailable in this browser. Select the text manually.");
        return;
      }
    }
    setCopied(key);
    setTimeout(() => setCopied(null), 1600);
  };

  const sendPreview = async () => {
    if (!campaign?.email_content_id || previewStatus === "sending") return;
    setPreviewStatus("sending");
    try {
      const { data } = await axios.post(`${API}/admin/send-preview`, { content_id: campaign.email_content_id }, { headers: headers(), withCredentials: true });
      setPreviewStatus(data.message);
    } catch (e) {
      setPreviewStatus(e.response?.data?.detail || "Preview failed.");
    }
  };

  const refineAsset = async (asset, feedback) => {
    if (!campaign || refining) return;
    setRefining(asset);
    setError(null);
    try {
      await axios.post(`${API}/wingman/campaigns/${campaign.campaign_id}/refine`, { asset, feedback }, { headers: headers(), withCredentials: true, timeout: 120000 });
      await openCampaign(campaign.campaign_id, { keepTab: true });
    } catch (e) {
      setError(e.response?.data?.detail || "Refinement failed — try a clearer instruction.");
    }
    setRefining(null);
  };

  const restoreVersion = async (index) => {
    if (!campaign || restoring !== null) return;
    setRestoring(index);
    setError(null);
    try {
      await axios.post(`${API}/wingman/campaigns/${campaign.campaign_id}/restore/${index}`, {}, { headers: headers(), withCredentials: true });
      await openCampaign(campaign.campaign_id, { keepTab: true });
    } catch (e) {
      setError(e.response?.data?.detail || "Could not restore that version.");
    }
    setRestoring(null);
  };

  const sendCampaignEmail = async () => {
    if (!campaign || sendState === "sending") return;
    setSendState("sending");
    try {
      const { data } = await axios.post(`${API}/wingman/campaigns/${campaign.campaign_id}/send-email`, {}, { headers: headers(), withCredentials: true, timeout: 120000 });
      setSendState(`Sent to ${data.delivered} of ${data.recipients} subscribers${data.failed ? ` — ${data.failed} failed` : ""}.`);
      await openCampaign(campaign.campaign_id, { keepTab: true });
    } catch (e) {
      setSendState(e.response?.data?.detail || "Send failed.");
    }
  };

  const asset = campaign?.campaign?.assets || {};
  const email = asset.email || {};
  const landing = asset.landing || {};
  const seo = asset.seo || {};
  const outreach = asset.outreach || {};
  const refinements = (campaign?.history || []).length;
  const lastSend = (campaign?.sends || []).slice(-1)[0];

  const refineProps = { onRefine: refineAsset, refining };

  const wizard = (
    <motion.div key="wizard" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.5, ease: EASE }}
      className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-xl p-6 md:p-8" data-testid="campaign-wizard">
      <div className="flex items-center gap-2 mb-8" data-testid="wizard-progress">
        {["Brief", "Voice", "Channels"].map((label, i) => (
          <button key={label} type="button" onClick={() => setStep(i)} data-testid={`wizard-step-${i}`}
            className={`flex-1 h-2 rounded-full transition-colors duration-300 ${i <= step ? "bg-white" : "bg-white/10"}`} aria-label={label} />
        ))}
      </div>

      {step === 0 && (
        <div className="space-y-5" data-testid="wizard-brief-step">
          <div>
            <label className={labelClass}>Campaign name</label>
            <input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="campaign-name-input" className={fieldClass} />
          </div>
          <div>
            <label className={labelClass}>Goal</label>
            <textarea value={form.goal} onChange={(e) => update("goal", e.target.value)} rows={3} data-testid="campaign-goal-input" className={`${fieldClass} resize-none`} />
          </div>
          <div>
            <label className={labelClass}>Audience</label>
            <textarea value={form.audience} onChange={(e) => update("audience", e.target.value)} rows={3} data-testid="campaign-audience-input" className={`${fieldClass} resize-none`} />
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5" data-testid="wizard-voice-step">
          <div>
            <label className={labelClass}>Brand voice</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="brand-voice-picker">
              {VOICES.map((voice) => (
                <button key={voice} type="button" onClick={() => update("voice", voice)} data-testid={`voice-${voice.toLowerCase().replaceAll(" ", "-")}`}
                  className={`rounded-full border px-4 py-3 text-sm text-left transition-colors duration-300 ${form.voice === voice ? "v4-btn-primary border-white" : "border-white/10 text-zinc-300 hover:bg-white/5"}`}>
                  {voice}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelClass}>Offer</label>
            <textarea value={form.offer} onChange={(e) => update("offer", e.target.value)} rows={3} data-testid="campaign-offer-input" className={`${fieldClass} resize-none`} />
          </div>
          <div>
            <label className={labelClass}>Proof and constraints</label>
            <textarea value={form.proof} onChange={(e) => update("proof", e.target.value)} rows={3} data-testid="campaign-proof-input" className={`${fieldClass} resize-none`} />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5" data-testid="wizard-channels-step">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={`${labelClass} mb-0`}>Channels</label>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-400" data-testid="channel-count">{form.channels.length} selected</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="channel-picker">
              {CHANNELS.map((channel) => (
                <button key={channel.id} type="button" onClick={() => toggleChannel(channel.id)} data-testid={`channel-${channel.id}`}
                  className={`rounded-full border px-3 py-2.5 text-sm transition-colors duration-300 ${form.channels.includes(channel.id) ? "v4-btn-primary border-white" : "border-white/10 text-zinc-300 hover:bg-white/5"}`}>
                  {channel.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelClass}>Launch date</label>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" data-testid="campaign-date-trigger"
                  className={`${fieldClass} flex items-center justify-between text-left`}>
                  <span>{selectedDate ? selectedDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Choose launch date"}</span>
                  <CalendarIcon className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="campaign-date-calendar">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => update("launch_date", date ? date.toISOString().slice(0, 10) : "")}
                  className="rounded-md"
                  classNames={{
                    caption_label: "text-sm font-medium text-white",
                    head_cell: "text-zinc-400 rounded-md w-8 font-normal text-[0.8rem]",
                    day: "h-8 w-8 p-0 font-normal text-zinc-300 hover:bg-white/10 rounded-md",
                    day_selected: "bg-white text-black hover:bg-white hover:text-black focus:bg-white focus:text-black rounded-md",
                    day_today: "border border-white/20 text-white rounded-md",
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <label className={labelClass}>Founder notes</label>
            <textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={3} placeholder="Launch detail, timing note, angle, or constraint…" data-testid="campaign-notes-input" className={`${fieldClass} resize-none`} />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mt-8">
        <button type="button" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="campaign-wizard-back"
          className="h-11 px-5 rounded-full border border-white/15 text-white text-sm font-medium disabled:opacity-30 hover:bg-white/10 transition-colors duration-300">
          Back
        </button>
        {step < 2 ? (
          <button type="button" onClick={() => setStep(step + 1)} disabled={!stepValid()} data-testid="campaign-wizard-next"
            className="h-11 px-5 rounded-full v4-btn-primary text-sm font-semibold disabled:opacity-30 hover:bg-zinc-200 transition-colors duration-300 flex items-center gap-2">
            Continue <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        ) : (
          <button type="button" onClick={generate} disabled={generating || !stepValid()} data-testid="campaign-generate-button"
            className="h-11 px-5 rounded-full v4-btn-primary text-sm font-semibold disabled:opacity-40 hover:bg-zinc-200 transition-colors duration-300 flex items-center gap-2">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
            {generating ? "Building campaign…" : "Generate campaign"}
          </button>
        )}
      </div>
    </motion.div>
  );

  const renderTab = () => {
    if (!campaign) return null;
    if (activeTab === "plan") {
      return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="tab-panel-plan">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6">
            <h3 className="font-display text-xl text-white mb-5">Launch checklist</h3>
            <div className="space-y-3">
              {campaign.checklist.map((item, i) => (
                <label key={item.task} className="flex items-start gap-3 border border-white/10 rounded-md p-3 cursor-pointer hover:bg-white/5 transition-colors duration-300" data-testid={`checklist-item-${i}`}>
                  <input type="checkbox" checked={item.done} onChange={(e) => toggleChecklist(i, e.target.checked)} data-testid={`checklist-toggle-${i}`}
                    className="mt-1 accent-emerald-400" />
                  <span>
                    <span className="block text-sm text-white">{item.task}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400">{item.day}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6">
            <h3 className="font-display text-xl text-white mb-5">Publish schedule</h3>
            <div className="space-y-3">
              {campaign.schedule.map((item, i) => (
                <div key={`${item.channel}-${i}`} className="flex items-center gap-4 border border-white/10 rounded-md p-3" data-testid={`schedule-item-${i}`}>
                  <CalendarDays className="w-4 h-4 text-emerald-400 shrink-0" strokeWidth={1.5} />
                  <span className="flex-1">
                    <span className="block text-sm text-white">{item.task}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400">{item.channel}</span>
                  </span>
                  <span className="font-mono text-xs text-zinc-400">{item.date} · {item.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
    if (activeTab === "schedule") {
      return <ScheduleTab campaign={campaign} />;
    }
    if (activeTab === "performance") {
      return <PerformanceTab campaign={campaign} headers={headers} onCampaign={setCampaign} />;
    }
    if (activeTab === "history") {
      return <HistoryTab campaign={campaign} onRestore={restoreVersion} restoring={restoring} />;
    }
    if (activeTab === "personas") {
      return (
        <div data-testid="tab-panel-personas">
          <div className="flex justify-end mb-4">
            <AssetRefineButton asset="personas" label="Refine personas" {...refineProps} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(campaign.campaign.personas || []).map((persona, i) => (
              <div key={persona.name || i} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-5" data-testid={`persona-card-${i}`}>
                <h3 className="font-display text-xl text-white mb-3">{persona.name}</h3>
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400 mb-2">Pain</p>
                <p className="text-sm text-zinc-300 leading-relaxed mb-4">{persona.pain}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400 mb-2">Message</p>
                <p className="text-sm text-zinc-300 leading-relaxed mb-4">{persona.message}</p>
                <button type="button" onClick={() => copy(`persona-cta-${i}`, persona.cta)} data-testid={`copy-persona-cta-${i}`}
                  className="text-xs text-emerald-400 hover:text-emerald-300 font-mono uppercase tracking-[0.14em]">{persona.cta}</button>
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (activeTab === "utm") {
      return (
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6" data-testid="tab-panel-utm">
          <h3 className="font-display text-xl text-white mb-5">UTM links</h3>
          <div className="space-y-3">
            {Object.entries(campaign.utm_links || {}).map(([channel, url]) => (
              <div key={channel} className="flex items-center gap-3 border border-white/10 rounded-md p-3" data-testid={`utm-${channel}`}>
                <LinkIcon className="w-4 h-4 text-emerald-400 shrink-0" strokeWidth={1.5} />
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400 w-28">{channel}</span>
                <span className="flex-1 font-mono text-xs text-zinc-300 truncate">{url}</span>
                <button type="button" onClick={() => copy(`utm-${channel}`, url)} data-testid={`copy-utm-${channel}`}
                  className="text-zinc-400 hover:text-white transition-colors duration-300"><Copy className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
            ))}
          </div>
          {campaignStats && (
            <div className="grid grid-cols-3 gap-3 mt-6" data-testid="campaign-live-funnel">
              <div><span className={labelClass}>Visits</span><span className="text-2xl text-white">{campaignStats.home_view || 0}</span></div>
              <div><span className={labelClass}>Checkouts</span><span className="text-2xl text-white">{campaignStats.checkout_start || 0}</span></div>
              <div><span className={labelClass}>Paid</span><span className="text-2xl text-white">{campaignStats.checkout_success || 0}</span></div>
            </div>
          )}
        </div>
      );
    }
    if (activeTab === "email") {
      return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="tab-panel-email">
          <div className="lg:col-span-2">
            <AssetCard title="Launch email" value={email.body} testid="asset-email" asset="email.body" copied={copied} onCopy={copy} {...refineProps} />
          </div>
          <div className="space-y-4">
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-5" data-testid="subject-variants">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-xl text-white">Subject variants</h3>
                <AssetRefineButton asset="email.subject_lines" {...refineProps} />
              </div>
              {(email.subject_lines || []).map((line, i) => (
                <button key={line} type="button" onClick={() => copy(`subject-${i}`, line)} data-testid={`subject-variant-${i}`}
                  className="w-full text-left border border-white/10 rounded-full px-4 py-2.5 mb-2 text-sm text-zinc-300 hover:bg-white/5 transition-colors duration-300">{line}</button>
              ))}
            </div>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-5" data-testid="cta-variants">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-xl text-white">CTA variants</h3>
                <AssetRefineButton asset="email.cta_variants" {...refineProps} />
              </div>
              {(email.cta_variants || []).map((line, i) => (
                <button key={line} type="button" onClick={() => copy(`cta-${i}`, line)} data-testid={`cta-variant-${i}`}
                  className="w-full text-left border border-white/10 rounded-full px-4 py-2.5 mb-2 text-sm text-zinc-300 hover:bg-white/5 transition-colors duration-300">{line}</button>
              ))}
            </div>
            {campaign.email_content_id && (
              <button type="button" onClick={sendPreview} disabled={previewStatus === "sending"} data-testid="send-preview-button"
                className="w-full h-11 rounded-full border border-white/15 text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-white/10 disabled:opacity-40 transition-colors duration-300">
                {previewStatus === "sending" ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Mail className="w-4 h-4" strokeWidth={1.5} />}
                Send branded preview
              </button>
            )}
            {previewStatus && previewStatus !== "sending" && <p className="font-mono text-xs text-zinc-400" data-testid="preview-status">{previewStatus}</p>}
            {email.body && (
              <div className="bg-[#0A0A0A] border border-emerald-400/20 rounded-lg p-5" data-testid="send-list-panel">
                <h3 className="font-display text-xl text-white mb-2">Send to subscribers</h3>
                <p className="text-xs text-zinc-400 leading-relaxed mb-4">
                  Delivers this email to everyone on the Doclyx list, branded, with the campaign UTM link. One send per 6 hours.
                </p>
                {sendState === "confirm" ? (
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={sendCampaignEmail} data-testid="send-list-confirm"
                      className="flex-1 h-11 rounded-full v4-btn-primary text-sm font-semibold flex items-center justify-center gap-2 transition-colors duration-300">
                      <Send className="w-4 h-4" strokeWidth={1.5} /> Confirm send
                    </button>
                    <button type="button" onClick={() => setSendState(null)} data-testid="send-list-cancel"
                      className="h-11 px-5 rounded-full border border-white/15 text-white text-sm font-medium hover:bg-white/10 transition-colors duration-300">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setSendState("confirm")} disabled={sendState === "sending"} data-testid="send-list-button"
                    className="w-full h-11 rounded-full v4-btn-primary text-sm font-semibold flex items-center justify-center gap-2 hover:bg-zinc-200 disabled:opacity-40 transition-colors duration-300">
                    {sendState === "sending" ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
                    {sendState === "sending" ? "Sending…" : "Send to the list"}
                  </button>
                )}
                {sendState && sendState !== "confirm" && sendState !== "sending" && (
                  <p className="font-mono text-xs text-emerald-400 mt-3" data-testid="send-list-status">{sendState}</p>
                )}
                {lastSend && (
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-3" data-testid="send-list-last">
                    Last sent {new Date(lastSend.at).toLocaleString()} — {lastSend.delivered}/{lastSend.recipients} delivered
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
    if (activeTab === "ads") {
      return (
        <div data-testid="tab-panel-ads">
          <div className="flex justify-end mb-4">
            <AssetRefineButton asset="ads" label="Refine ad set" {...refineProps} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(asset.ads || []).map((ad, i) => (
              <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-5" data-testid={`ad-card-${i}`}>
                <h3 className="font-display text-xl text-white mb-3">{ad.headline}</h3>
                <p className="text-sm text-zinc-300 leading-relaxed mb-4">{ad.body}</p>
                <button type="button" onClick={() => copy(`ad-${i}`, `${ad.headline}\n\n${ad.body}\n${ad.cta}`)} data-testid={`copy-ad-${i}`}
                  className="text-xs font-mono uppercase tracking-[0.14em] text-emerald-400">{ad.cta}</button>
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (activeTab === "landing") {
      return (
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-8" data-testid="tab-panel-landing">
          <div className="flex items-center justify-between mb-4">
            <span className={`${labelClass} mb-0`}>Landing hero</span>
            <AssetRefineButton asset="landing" label="Refine hero" {...refineProps} />
          </div>
          <h3 className="font-display text-3xl text-white mb-3">{landing.headline}</h3>
          <p className="text-zinc-300 leading-relaxed mb-6">{landing.subheadline}</p>
          <div className="space-y-2 mb-6">{(landing.bullets || []).map((b, i) => <p key={i} className="text-sm text-zinc-300" data-testid={`landing-bullet-${i}`}>— {b}</p>)}</div>
          <button type="button" onClick={() => copy("landing", landing)} data-testid="copy-landing" className="h-10 px-5 rounded-full v4-btn-primary text-sm font-semibold">Copy landing copy</button>
        </div>
      );
    }
    if (activeTab === "seo") {
      return (
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6" data-testid="tab-panel-seo">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-2xl text-white">{seo.title}</h3>
            <AssetRefineButton asset="seo" label="Refine brief" {...refineProps} />
          </div>
          <div className="flex flex-wrap gap-2 mb-6">{(seo.keywords || []).map((k) => <span key={k} className="font-mono text-[10px] uppercase tracking-[0.14em] border border-white/10 rounded-full px-3 py-1.5 text-zinc-300">{k}</span>)}</div>
          <div className="space-y-3">{(seo.outline || []).map((item, i) => <p key={i} className="text-sm text-zinc-300" data-testid={`seo-outline-${i}`}>{i + 1}. {item}</p>)}</div>
        </div>
      );
    }
    if (activeTab === "outreach") {
      return <AssetCard title={`Outreach — ${outreach.subject || "Founder note"}`} value={outreach.body} testid="asset-outreach" asset="outreach" copied={copied} onCopy={copy} {...refineProps} />;
    }
    const channelCard = <AssetCard title={CHANNELS.find((c) => c.id === activeTab)?.label || activeTab} value={asset[activeTab]} testid={`asset-${activeTab}`} asset={activeTab} copied={copied} onCopy={copy} {...refineProps} />;
    if (activeTab === "linkedin" || activeTab === "x") {
      return (
        <div>
          {channelCard}
          <ShareImagePanel campaign={campaign} channel={activeTab} headers={headers} onCampaign={setCampaign} />
        </div>
      );
    }
    return channelCard;
  };

  return (
    <main className="v4-theme bg-page text-ink min-h-screen px-5 py-8 md:px-8 md:py-10" data-testid="admin-wingman-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <Link to="/admin" data-testid="admin-back-link"><Logo dark /></Link>
          <Link to="/admin" className="flex items-center gap-2 text-sm text-fog hover:text-ink transition-colors duration-300" data-testid="command-center-link">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Command center
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3 space-y-4 lg:sticky lg:top-8 self-start" data-testid="campaign-sidebar">
            <div className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-lg p-5">
              <span className={labelClass}>Saved campaigns</span>
              <div className="space-y-2 mt-4">
                {campaigns.length === 0 && <p className="text-sm text-zinc-400" data-testid="no-campaigns">No campaigns yet.</p>}
                {campaigns.map((item) => (
                  <button key={item.campaign_id} type="button" onClick={() => openSaved(item.campaign_id)} data-testid={`saved-campaign-${item.campaign_id}`}
                    className={`w-full text-left border rounded-full px-4 py-3 transition-colors duration-300 ${campaign?.campaign_id === item.campaign_id ? "border-white/30 bg-white/10" : "border-white/10 hover:bg-white/5"}`}>
                    <span className="block text-sm text-white truncate">{item.name}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400">{item.status} · {new Date(item.created_at).toLocaleDateString()}</span>
                  </button>
                ))}
              </div>
            </div>
            {campaign && (
              <div className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-lg p-5" data-testid="campaign-status-panel">
                <span className={labelClass}>Campaign status</span>
                <select value={campaign.status} onChange={(e) => saveStatus(e.target.value)} disabled={statusSaving} data-testid="campaign-status-select"
                  className="mt-3 w-full h-11 rounded-md bg-white/5 border border-white/10 px-4 text-sm text-white focus:outline-none focus:border-white/30">
                  {STATUS_OPTIONS.map((status) => <option key={status} value={status} className="bg-[#0A0A0A]">{status}</option>)}
                </select>
              </div>
            )}
          </aside>

          <section className="lg:col-span-9">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-4">Marketing studio V5</span>
              <h1 className="font-display font-medium tracking-tight text-4xl md:text-5xl mb-3" data-testid="admin-wingman-title">Build, refine, ship, measure.</h1>
              <p className="text-fog leading-relaxed max-w-3xl mb-10">
                One brief becomes channel copy, personas, UTM links, checklist and schedule. Refine any asset with Wingman,
                send the launch email to your list, then watch the live funnel and double down where the numbers point.
              </p>

              {error && <p className="font-mono text-xs text-[#FF3333] mb-6" data-testid="campaign-error">{error}</p>}

              <AnimatePresence mode="wait">{!campaign && wizard}</AnimatePresence>

              {campaign && (
                <motion.div key="results" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} data-testid="campaign-results">
                  <div className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-lg p-5 mb-6 flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <span className={labelClass}>
                        {campaign.status} campaign{refinements > 0 ? ` · ${refinements} refinement${refinements === 1 ? "" : "s"}` : ""}
                      </span>
                      <h2 className="font-display text-2xl text-white" data-testid="campaign-title">{campaign.campaign.campaign_name || campaign.name}</h2>
                    </div>
                    <button type="button" onClick={() => { setCampaign(null); setStep(0); setSendState(null); }} data-testid="new-campaign-button"
                      className="h-10 px-5 rounded-full border border-white/15 text-white text-sm font-medium hover:bg-white/10 transition-colors duration-300">
                      New campaign
                    </button>
                  </div>

                  <div className="mb-6" data-testid="campaign-positioning-card">
                    <AssetCard title="Positioning" value={campaign.campaign.positioning} testid="campaign-positioning" asset="positioning" copied={copied} onCopy={copy} {...refineProps} />
                  </div>

                  <div className="flex gap-2 overflow-x-auto pb-2 mb-6" data-testid="campaign-tabs">
                    {tabs.map((tab) => (
                      <button key={tab} type="button" onClick={() => setActiveTab(tab)} data-testid={`tab-${tab}`}
                        className={`shrink-0 rounded-full border px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors duration-300 ${activeTab === tab ? "v4-btn-primary border-white" : "border-white/10 text-zinc-400 hover:text-white hover:bg-white/5"}`}>
                        {tab === "utm" ? "UTM" : tab}
                      </button>
                    ))}
                  </div>

                  <AnimatePresence mode="wait">
                    <motion.div key={activeTab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3, ease: EASE }}>
                      {renderTab()}
                    </motion.div>
                  </AnimatePresence>
                </motion.div>
              )}
            </motion.div>
          </section>
        </div>
      </div>
    </main>
  );
}

const AssetRefineButton = ({ asset, label = "Refine", onRefine, refining }) => {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const busy = refining === asset;

  const submit = async () => {
    if (feedback.trim().length < 3 || busy) return;
    await onRefine(asset, feedback.trim());
    setFeedback("");
    setOpen(false);
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid={`refine-open-${asset.replace(/[._]/g, "-")}`}
        className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white border border-white/10 rounded-full px-3 py-1.5 shrink-0 transition-colors duration-300">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />}
        {busy ? "Refining…" : label}
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 w-full" data-testid={`refine-panel-${asset.replace(/[._]/g, "-")}`}>
      <input
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder="What should change?"
        data-testid={`refine-input-${asset.replace(/[._]/g, "-")}`}
        className="flex-1 min-w-0 h-9 rounded-full bg-white/5 border border-white/10 px-4 text-xs text-white placeholder:text-zinc-500 focus:outline-none focus:border-white/30"
      />
      <button type="button" onClick={submit} disabled={busy || feedback.trim().length < 3} data-testid={`refine-submit-${asset.replace(/[._]/g, "-")}`}
        className="h-9 px-4 rounded-full v4-btn-primary text-xs font-semibold shrink-0 disabled:opacity-40 transition-colors duration-300">
        Apply
      </button>
      <button type="button" onClick={() => setOpen(false)} data-testid={`refine-cancel-${asset.replace(/[._]/g, "-")}`}
        className="text-xs text-zinc-400 hover:text-white shrink-0 transition-colors duration-300">
        Cancel
      </button>
    </div>
  );
};
